import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Container } from '../../packages/core/di/Container';
import { i18n } from '../i18n';
import type { RegisterMovementUseCase } from '../../packages/core/application';
import type { RegisterMovementDTO } from '../../packages/core/application';
import type { MovementResponseDTO } from '../../packages/core/application';
import type { MovementRepository } from '../../packages/core/domain/interfaces/MovementRepository';

export const useMovementStore = defineStore('movement', () => {
  // State
  const movements = ref<MovementResponseDTO[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const pendingMovements = ref<MovementResponseDTO[]>([]);

  // Getters
  const movementsByProduct = computed(() => (productId: string) => 
    movements.value.filter(m => m.productId === productId)
  );
  
  const movementsByWarehouse = computed(() => (warehouseId: string) => 
    movements.value.filter(m => m.warehouseId === warehouseId)
  );
  
  const recentMovements = computed(() => 
    movements.value
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, 10)
  );

  // Actions
  const container = Container.getInstance();

  const registerMovement = async (dto: RegisterMovementDTO) => {
    loading.value = true;
    error.value = null;
    
    try {
      const registerMovementUseCase = container.get<RegisterMovementUseCase>('RegisterMovementUseCase');
      const newMovement = await registerMovementUseCase.execute(dto);
      
      movements.value.unshift(newMovement);
      return newMovement;
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.registerMovement');
      console.error('Error registering movement:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchMovements = async () => {
    loading.value = true;
    error.value = null;
    try {
      const repo = container.get<MovementRepository>('MovementRepository');
      const entities = await repo.findAll();
      movements.value = entities.map((m) => ({
          id: m._id,
          movementType: m.movementType,
          productId: m.productId,
          warehouseId: m.warehouseId,
          quantity: m.quantity,
          unitCost: m.unitCost,
          unitPrice: m.unitPrice,
          status: m.status,
          reference: m.reference,
          reason: m.reason,
          notes: m.notes,
          destinationWarehouseId: m.destinationWarehouseId,
          createdAt: m.createdAt,
          updatedAt: m.updatedAt,
        } as MovementResponseDTO));
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.fetchMovements');
      console.error('Error fetching movements:', err);
    } finally {
      loading.value = false;
    }
  };

  const addPendingMovement = (movement: MovementResponseDTO) => {
    pendingMovements.value.push(movement);
  };

  const removePendingMovement = (movementId: string) => {
    const index = pendingMovements.value.findIndex(m => m.id === movementId);
    if (index !== -1) {
      pendingMovements.value.splice(index, 1);
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const reset = () => {
    movements.value = [];
    loading.value = false;
    error.value = null;
    pendingMovements.value = [];
  };

  return {
    // State
    movements,
    loading,
    error,
    pendingMovements,
    
    // Getters
    movementsByProduct,
    movementsByWarehouse,
    recentMovements,
    
    // Actions
    fetchMovements,
    registerMovement,
    addPendingMovement,
    removePendingMovement,
    clearError,
    reset
  };
});
